// Mock data for PNX Coatings (formerly Phoenix Decorative Surfaces)

const YEARS_OF_EXPERIENCE = 30;

// Client-provided project photography (hosted locally under /public/images)
const IMG = {
  epoxy: "/images/epoxy.jpg",
  epoxyResinous: "/images/epoxy-resinous.jpg",
  foodBeverage: "/images/food-beverage.jpg",
  microTopping: "/images/micro-topping.jpg",
  polishedConcrete: "/images/polished-concrete-new.jpg",
  concreteRestoration: "/images/concrete-restoration.jpg",
  industrial: "/images/industrial.jpg",
  parking: "/images/parking.jpg",
  polishedTopping: "/images/polished-topping.jpg",
  decorativeOverlays: "/images/decorative-overlays.jpg",
  stadium: "/images/highmark-stadium.jpg",
  moisture: "/images/moisture-mitigation.jpg",
};

export const company = {
  name: "Phoenix Decorative Surfaces, Western New York Division",
  shortName: "Phoenix",
  tagline: "Industrial-Grade Floor Coating Solutions for Large-Scale Facilities",
  phone: "716-657-1225",
  fax: "716-657-1230",
  hours: "8:30am - 4:30pm M-F",
  area: "Serving Western New York",
  address: "2810 Sweet Home Road, Amherst, NY 14228",
  email: "info@phoenix-wny.com",
  yearsExperience: YEARS_OF_EXPERIENCE,
  social: {
    facebook: "https://www.facebook.com/pnx.deco/",
    instagram: "https://www.instagram.com/pnx.deco",
    twitter: "https://x.com/PNXDeco",
    youtube: "https://www.youtube.com/@PhoenixDecoS",
  },
};

export const stats = [
  { value: "30+", label: "Years of Experience" },
  { value: "500K+", label: "Sq Ft Coated Annually" },
  { value: "24/7", label: "Off-Hours Scheduling" },
  { value: "100%", label: "Client Satisfaction" },
];

export const heroSlides = [
  {
    title: "Industrial Floor Coatings Built for Large Facilities",
    subtitle:
      "High-performance epoxy and resinous systems engineered for stadiums, data centers, manufacturing plants and distribution centers \u2014 installed with minimal downtime.",
    image: IMG.industrial,
  },
];

// Highlighted services shown on the home page
export const featuredServices = [
  {
    id: "epoxy-resinous",
    title: "Epoxy & Resinous Flooring",
    short:
      "High-performance, chemical-resistant systems built to withstand heavy foot traffic, forklifts and equipment loads in demanding facilities.",
    image: IMG.epoxyResinous,
  },
  {
    id: "commercial-industrial",
    title: "Manufacturing & Plant Floors",
    short:
      "Industrial-grade coatings engineered for production floors, assembly lines and process areas where durability is non-negotiable.",
    image: IMG.industrial,
  },
  {
    id: "polished-concrete",
    title: "Polished Concrete",
    short:
      "Densified, low-maintenance polished slabs ideal for warehouses, distribution centers and large retail footprints.",
    image: IMG.polishedConcrete,
  },
  {
    id: "moisture-mitigation",
    title: "Moisture Mitigation & ESD",
    short:
      "Vapor mitigation and static-control systems that protect sensitive equipment in data centers, labs and electronics facilities.",
    image: IMG.moisture,
  },
  {
    id: "concrete-restorations",
    title: "Concrete Restorations",
    short:
      "Repair and resurface worn, damaged and high-traffic slabs to extend the life of your facility's floors.",
    image: IMG.concreteRestoration,
  },
  {
    id: "interior-exterior",
    title: "Traffic Deck & Exterior Coatings",
    short:
      "Slip-resistant, UV-stable systems for parking structures, ramps, loading docks and exterior walkways.",
    image: IMG.parking,
  },
];

// Full service catalog for the Services page
export const services = [
  {
    id: "epoxy-resinous",
    title: "Epoxy & Resinous Flooring",
    image: IMG.epoxyResinous,
    body:
      "With over 30 years installing hundreds of thousands of square feet of resinous coatings each year, Phoenix delivers high-performance epoxy and resinous systems that resist chemicals, abrasion and heavy impact. Ideal for manufacturing floors, warehouses, hangars and process areas that demand a seamless, long-lasting surface.",
  },
  {
    id: "industrial-coatings",
    title: "Heavy-Duty Industrial Coatings",
    image: IMG.industrial,
    body:
      "Engineered for the harshest environments, our heavy-duty systems stand up to forklift traffic, thermal cycling, chemical exposure and constant loads. We spec the right buildup \u2014 from broadcast quartz to troweled mortar systems \u2014 for production plants, foundries and industrial facilities.",
  },
  {
    id: "polished-concrete",
    title: "Polished Concrete",
    image: IMG.polishedConcrete,
    body:
      "Densified, diamond-polished concrete is the go-to for warehouses, distribution centers and large retail footprints. It delivers a bright, dust-proof, low-maintenance surface that handles heavy traffic and reflects light to reduce lighting costs across large facilities.",
  },
  {
    id: "esd-flooring",
    title: "ESD & Static-Control Flooring",
    image: IMG.epoxy,
    body:
      "Protect sensitive equipment and processes with electrostatic-dissipative (ESD) and conductive flooring systems. Essential for data centers, electronics manufacturing, labs and clean rooms where static discharge can damage critical infrastructure.",
  },
  {
    id: "food-grade",
    title: "Food & Beverage / Sanitary Flooring",
    image: IMG.foodBeverage,
    body:
      "Seamless, antimicrobial urethane-cement and epoxy systems built for commercial kitchens, food and beverage plants, breweries and healthcare facilities. Thermal-shock resistant, USDA-friendly and easy to sanitize for wash-down environments.",
  },
  {
    id: "decorative-overlays",
    title: "Decorative Overlays",
    image: IMG.decorativeOverlays,
    body:
      "Give arenas, lobbies, concourses and retail spaces a premium look without a full tear-out. Decorative overlays add a thin new layer of specialized concrete that can be stained, stamped, scored and seeded with aggregates to achieve virtually any high-traffic finish.",
  },
  {
    id: "micro-toppings",
    title: "Micro Toppings",
    image: IMG.microTopping,
    body:
      "Micro toppings are specialized overlays applied in very thin coats to refresh sound but worn surfaces across large square footage. They create a seamless, contemporary, extremely hard finish \u2014 ideal for modernizing existing commercial floors quickly.",
  },
  {
    id: "concrete-restorations",
    title: "Concrete Restorations",
    image: IMG.concreteRestoration,
    body:
      "Extend the life of your facility's slabs. We repair pop-outs, divots, cracks, spalling, joints and high-traffic wear, then rebuild and profile the substrate \u2014 providing a stable base ready for polishing, coatings or toppings with minimal operational disruption.",
  },
  {
    id: "moisture-mitigation",
    title: "Moisture Mitigation",
    image: IMG.moisture,
    body:
      "Moisture-related failures can shut down a facility. Our vapor mitigation systems control water vapor from groundwater and from the slab itself \u2014 such as off-gassing from insufficiently cured concrete \u2014 protecting large-scale coating and flooring investments for the long term.",
  },
  {
    id: "traffic-coatings",
    title: "Traffic Deck & Parking Coatings",
    image: IMG.parking,
    body:
      "Waterproofing and traffic-bearing membrane systems for parking structures, ramps, loading docks and exterior walkways. Slip-resistant and UV-stable, they protect structural concrete while providing safe, durable surfaces for vehicles and pedestrians.",
  },
];

// "Why choose us" feature highlights
export const features = [
  { title: "Minimal Downtime", text: "Off-hours, weekend and phased scheduling with fast-cure systems keep your operations running." },
  { title: "Heavy-Duty Performance", text: "Built to withstand forklifts, chemical exposure, thermal cycling and constant heavy loads." },
  { title: "Large-Scale Capacity", text: "Crews, equipment and logistics to deliver projects spanning 100,000+ square feet." },
  { title: "Manufacturer Certified", text: "Certified installers with full written data sheets and top-tier manufacturer backing." },
  { title: "Safety & Compliance", text: "Slip-resistant, ESD, USDA and OSHA-conscious systems for regulated environments." },
  { title: "Turnkey Project Management", text: "Single point of contact from assessment and prep through final inspection and handover." },
];

export const galleryCategories = [
  "All",
  "Industrial",
  "Resinous / Epoxy",
  "Polished Concrete",
  "Traffic & Exterior",
];

export const galleryItems = [
  { id: 1, category: "Resinous / Epoxy", title: "Cleanroom Epoxy Floor", image: IMG.epoxy },
  { id: 2, category: "Industrial", title: "Industrial Corridor Coating", image: IMG.industrial },
  { id: 3, category: "Industrial", title: "Commercial Kitchen Floor", image: IMG.foodBeverage },
  { id: 4, category: "Traffic & Exterior", title: "Parking Deck Coating", image: IMG.parking },
  { id: 5, category: "Polished Concrete", title: "Polished Concrete Finish", image: IMG.polishedConcrete },
  { id: 6, category: "Polished Concrete", title: "Polished Topping", image: IMG.polishedTopping },
  { id: 7, category: "Polished Concrete", title: "Micro Topping Finish", image: IMG.microTopping },
];

export const philosophy = {
  intro:
    "Phoenix is a family-owned company with over 30 years of experience in epoxy and resinous coatings within the heavy commercial and industrial sectors. We specialize in large-scale facilities \u2014 stadiums, data centers, manufacturing plants, warehouses and distribution centers \u2014 across Western New York.",
  mission:
    "Leveraging our extensive industrial background, we employ cutting-edge technology and superior products designed to withstand the most demanding applications. We are committed to advanced systems, competitive pricing and exceptional service that keeps your facility running.",
  vision:
    "To be the trusted flooring partner for the region's most demanding facilities \u2014 delivering durable, high-performance surfaces that protect critical operations for decades.",
};

export const values = [
  { title: "Family-Owned", text: "Three decades of hands-on, accountable service you can rely on." },
  { title: "Manufacturer Certified", text: "Certified and recommended by many respected coating manufacturers." },
  { title: "Industrial-Grade", text: "Systems engineered for the loads, chemicals and traffic of large facilities." },
  { title: "Minimal Disruption", text: "Flexible off-hours scheduling to keep your operations moving." },
];

export const testimonials = [
  { name: "Michael Reyes", role: "Facilities Director, Sports & Entertainment Arena", text: "Phoenix coated our concourse and back-of-house floors during a tight off-season window. The crew worked around the clock, and the finish has held up beautifully through packed events and heavy foot traffic." },
  { name: "Jennifer Osei", role: "Operations Manager, Data Center", text: "Their ESD and moisture mitigation systems were exactly what our facility needed. Meticulous surface prep, clear documentation, and zero impact to our uptime. A true professional partner." },
  { name: "David Kowalski", role: "Plant Manager, Manufacturing Facility", text: "We put our floors through brutal conditions \u2014 forklifts, chemicals, thermal shock. Phoenix specified the right heavy-duty system and it's performing exactly as promised. Highly recommended." },
  { name: "Sandra Whitfield", role: "Director of Facilities, Distribution Center", text: "Over 120,000 square feet of polished concrete delivered on schedule and on budget. The team's logistics and project management were outstanding from start to finish." },
];

export const steps = [
  { num: "01", title: "Site Assessment & Planning", text: "We evaluate your slab, environment and operations, then engineer the right system and a phased schedule that minimizes downtime." },
  { num: "02", title: "Surface Prep & Mitigation", text: "Diamond grinding, shot-blasting, repairs and moisture mitigation create a sound, profiled base for a lasting installation." },
  { num: "03", title: "Precision Installation", text: "Our trained crews install your system with meticulous attention to detail \u2014 often during off-hours to keep your facility running." },
  { num: "04", title: "Inspection & Handover", text: "A final walkthrough, documentation and maintenance guidance ensure your new floor performs for the long term." },
];

// Industries targeted. `icon` maps to a lucide-react icon in the Industries section.
export const industries = [
  { icon: "building", name: "Stadiums & Arenas", text: "Concourses, back-of-house and high-traffic public areas." },
  { icon: "server", name: "Data Centers", text: "ESD, static-control and moisture-mitigation systems." },
  { icon: "factory", name: "Manufacturing Plants", text: "Heavy-duty coatings for production and process floors." },
  { icon: "warehouse", name: "Warehouses & Distribution", text: "Durable polished concrete and resinous floors at scale." },
  { icon: "hospital", name: "Healthcare & Labs", text: "Seamless, sanitary and antimicrobial flooring systems." },
  { icon: "utensils", name: "Food & Beverage", text: "USDA-friendly urethane-cement wash-down floors." },
  { icon: "plane", name: "Aviation & Hangars", text: "Fuel- and chemical-resistant high-load coatings." },
  { icon: "parking", name: "Parking Structures", text: "Waterproofing and traffic-bearing deck coatings." },
];


// Notable completed / in-progress projects
export const notableProjects = [
  {
    id: "highmark-stadium",
    name: "Highmark Stadium",
    subtitle: "New Buffalo Bills Stadium \u2014 Orchard Park, NY",
    tag: "Stadium & Arena",
    text:
      "Phoenix is proud to be part of the team delivering specialized architectural concrete flooring systems for Highmark Stadium, the new home of the Buffalo Bills. Our scope includes stained and polished concrete, sealed concourse surfaces, custom decorative overlays, and back-of-house areas built to withstand heavy game-day traffic for decades.",
    text2:
      "Working directly with the material manufacturer, Phoenix helped develop a specialized decorative topping designed to withstand severe weather and extreme temperature fluctuations while providing a durable, polished, and aesthetically refined finish built for the demands of a world-class stadium.",
    image: "/images/highmark-stadium.jpg",
  },
  {
    id: "data-center",
    name: "Data Center & Mission-Critical Facilities",
    subtitle: "Server Rooms · ESD & Polished Concrete Systems",
    tag: "Data Center",
    text:
      "Phoenix delivers high-performance flooring solutions for data centers and other mission-critical facilities where durability, cleanliness, electrical safety, and long-term reliability are essential. Our capabilities include polished and sealed concrete, ESD and static-control resinous flooring systems, and seamless protective finishes designed to help control electrostatic discharge and support sensitive electronic environments.",
    text2:
      "These systems are engineered to withstand heavy equipment, rolling loads, continuous operational traffic, and demanding maintenance requirements while providing a clean, professional, and easy-to-maintain surface. From server rooms and equipment areas to corridors, support spaces, and back-of-house operations, Phoenix provides flooring solutions built for consistent performance in facilities that cannot afford downtime.",
    image: "/images/data-center.jpg",
  },
];
